import { createApi } from "@reduxjs/toolkit/query/react";
import { privateBaseQuery } from "../base/private/privateBaseQuery";
import { ApiEnvelope, UserEntity } from "@/types/canonical";
import type { SchoolContactRow } from "@/constants/supportContacts";

const SCHOOL_CONTACTS_FIELDS = "id,contacts:type,contacts:value,contacts:name";

type SchoolContactsResponse =
  | ApiEnvelope<{ contacts?: SchoolContactRow[] }>
  | { contacts?: SchoolContactRow[] };

export type UpdateMePayload = {
  id: number;
  school_id: number | null;
  auth_type: null;
  group: null;
  photo: string | null;
  photo_thumb: Record<string, unknown>;
  color: string | null;
  firstname: string | null;
  lastname: string | null;
  patronymic: string | null;
  email: string | null;
  phone: string | null;
  username: string | null;
  description: string | null;
  password: string | null;
  school: Record<string, unknown>;
  register_date: string | null;
  last_activity_date: string | null;
  additional: Record<string, unknown>;
  is_online: boolean;
  is_need_complete_challenge: boolean;
  is_multiple_schools: boolean;
};

/** Универсальный анвраппер {status, status_code, entity} -> entity */
const unwrap = <T>(res: T | ApiEnvelope<T>): T => {
  const maybe = res as ApiEnvelope<T>;
  return typeof maybe === "object" && maybe !== null && "entity" in maybe
    ? (maybe.entity as T)
    : (res as T);
};

export const authPrivateApi = createApi({
  reducerPath: "authPrivateApi",
  baseQuery: privateBaseQuery(),
  endpoints: (builder) => ({
    getUser: builder.query<UserEntity, void>({
      query: () => ({
        url: "/user.json?extended=1",
        method: "GET",
        headers: {
          "X-Requested-Fields":
            "id,school_id,firstname,lastname,patronymic,username,email,phone,photo,photo_thumb,color,description,group,school,school:languages,school:settings,register_date,last_activity_date,additional,is_online,is_need_complete_challenge,is_multiple_schools,status,teacher,student,external_id",
        },
      }),
      transformResponse: (res: ApiEnvelope<UserEntity> | UserEntity) =>
        unwrap<UserEntity>(res),
    }),

    updateMe: builder.mutation<UserEntity, UpdateMePayload>({
      query: (data) => ({
        url: "/user.json",
        method: "PUT",
        data,
        headers: {
          "X-Requested-Fields":
            "id,school_id,firstname,lastname,photo,email,phone,additional,password",
        },
      }),
      transformResponse: (res: ApiEnvelope<UserEntity> | UserEntity) =>
        unwrap<UserEntity>(res),
    }),

    /* Контакты школы текущего пользователя (school_contacts). /user.json вложенное
       school:contacts не отдаёт, а GET /school.json отвечает и студенту своей школы. */
    getMySchoolContacts: builder.query<SchoolContactRow[], void>({
      query: () => ({
        url: "/school.json",
        method: "GET",
        headers: { "X-Requested-Fields": SCHOOL_CONTACTS_FIELDS },
      }),
      transformResponse: (res: SchoolContactsResponse) =>
        unwrap(res)?.contacts ?? [],
    }),

    /* /school.json только читает — пишет PUT /schools/{id}. Его репозиторий берёт
       пропущенное поле из умолчаний (имя «Sample Company», contacts = []), поэтому
       уходит полный снимок user.school и все строки контактов, включая чужие. */
    updateMySchoolContacts: builder.mutation<
      SchoolContactRow[],
      { school: UserEntity["school"]; contacts: SchoolContactRow[] }
    >({
      query: ({ school, contacts }) => ({
        url: `/schools/${school.id}`,
        method: "PUT",
        data: { ...school, contacts },
        headers: { "X-Requested-Fields": SCHOOL_CONTACTS_FIELDS },
      }),
      transformResponse: (res: SchoolContactsResponse) =>
        unwrap(res)?.contacts ?? [],
      /* Кэш — из ответа, а не из запроса: показываем то, что легло в базу. */
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            authPrivateApi.util.upsertQueryData("getMySchoolContacts", undefined, data)
          );
        } catch {
          // ошибку показывает вызывающий через unwrap()
        }
      },
    }),

    checkAuth: builder.query<Record<string, unknown>, void>({
      query: () => ({
        url: "/auth/check",
        method: "GET",
      }),
      transformResponse: (
        res: ApiEnvelope<Record<string, unknown>> | Record<string, unknown>
      ) => unwrap<Record<string, unknown>>(res),
    }),

    logout: builder.mutation<void, void>({
      query: () => ({
        url: "/auth/logout",
        method: "POST",
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          localStorage.clear();

          if (typeof window !== "undefined") {
            // storage event does NOT fire in the same tab
            window.dispatchEvent(new Event("auth-token-changed"));
            // Reset RTK Query cache to clear user data
            dispatch(authPrivateApi.util.resetApiState());
            window.location.href = "/auth";
          }
        } catch {
          localStorage.clear();

          if (typeof window !== "undefined") {
            window.dispatchEvent(new Event("auth-token-changed"));
            // Reset RTK Query cache to clear user data
            dispatch(authPrivateApi.util.resetApiState());
          }
        }
      },
    }),
  }),
});

export const {
  useGetUserQuery,
  useLazyGetUserQuery,
  useUpdateMeMutation,
  useGetMySchoolContactsQuery,
  useUpdateMySchoolContactsMutation,
  useCheckAuthQuery,
  useLogoutMutation,
} = authPrivateApi;
